import React, { useState, useMemo } from 'react';
import ProductSwatches from '@/features/pdp/components/ProductSwatches';
import PersistentAddToCart from '@/features/pdp/components/PersistentAddToCart';
import useVariation from '@hooks/useVariation';
import ProductInfoContainer from '@/features/pdp/components/ProductInfoContainer';
import Description from '@/features/pdp/components/Description';
import ProductHeader from '@/features/pdp/components/ProductHeader';
import ProductPricing from '@/features/pdp/components/ProductPricing';
import SectionsNav from '@/features/pdp/components/SectionsNav';
import ProductShipping from '@/components/tailwind/ProductShipping';
import ProductAvailability from '@/components/tailwind/ProductAvailability';
import AddToCart from '@/components/AddToCart';
import ProductImageSlider from '../../../components/ProductImageSlider';
import DrinkwareSubtitle from '@/features/pdp/components/DrinkwareSubtitle';
import ProductsSlider from '@/components/tailwind/ProductsSlider';
import LoadingSpinner from '@/components/tailwind/LoadingSpinner';
import ErrorBoundary from '@/components/tailwind/ErrorBoundary';
import { SHOW_LOADER_DELAY, Breakpoints } from '@/settings';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@/store';
import ProductReviews from '@/components/ProductReviews';
import ProductPromoBar from '@/components/ProductPromoBar';
import ProductDiscountBanner from '@/components/tailwind/ProductDiscountBanner';
import FindYourFit from '@/components/FindYourFit';
import { BundleChoices } from '@/components/BundleChoice';
import ProductBundleBar from '@/components/ProductBundleBar';
import SectionScroll from '@/features/pdp/components/SectionScroll';
import Dimensions from '@features/pdp/components/Dimensions';
import { CgRuler } from 'react-icons/cg';
import stripHTML from '@/utils/stripHTML';
import NBCFLabel from '@/components/tailwind/NBCFLabel';
import { PDPViews } from '@types';
import PartnerDesignsView from '@features/pdp/components/PartnerDesignsView';
import { BsGear } from 'react-icons/bs';
import AnimatedSlider from '@/features/pdp/components/AnimatedSlider';
import MobileHeader from '@/features/pdp/components/MobileHeader';
import { useMediaQuery } from 'react-responsive';
import { lazyRetry } from '@/utils/lazyRetry';
import FreeGiftCta from '../../../components/tailwind/FreeGiftCta';
import EditPersonalization from './EditPersonalization';
import Breadcrumbs from '../../../components/tailwind/Breadcrumbs';
import StickySection from '@/components/tailwind/StickySection';
import { AnimatedSlideTemplate } from '@features/pdp/components/AnimatedSlider/AnimatedSlide';
import { MIYO_DISABLED_COLORS } from '../settings';
import { setView } from '../../../actions/pdp.actions';
import Section from '@features/cms/components/Section';
import AccessoryUpsell from '@components/tailwind/AccessoryUpsell';
import Affirm from './ProductPricing/Affirm';
import ProductWarranty from '@components/tailwind/ProductWarranty';
import cx from '@utils/cx.util';
import { Link } from '@components/Link';

const CustomDesigner = React.lazy(() =>
  lazyRetry(
    () => import(/* webpackChunkName: "CustomDesigner" */ '@/pages/ProductDetailsDesigner'),
    'CustomDesigner'
  )
);

const TieredDiscount = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        /* webpackChunkName: "TieredDiscount" */ '@/features/pdp/components/TieredDiscount/TieredDiscount'
      ),
    'TieredDiscount'
  )
);

const BundleDesigner = React.lazy(() =>
  lazyRetry(
    () => import(/* webpackChunkName: "CustomDesigner" */ '@/features/pdp/designer/BundleDesigner'),
    'BundleDesigner'
  )
);

const BulkDesigner = React.lazy(() =>
  lazyRetry(
    () => import(/* webpackChunkName: "BulkDesigner" */ '@/features/pdp/designer/BulkDesigner'),
    'BulkDesigner'
  )
);

type ProductDetailsProps = {
  klaviyoConfigured: boolean;
  v2Data: any;
  bundleInfo?: any;
  onChoiceChange?: any;
  isV2: boolean;
  crumbs: any;
};
const ProductDetails = ({
  klaviyoConfigured,
  v2Data,
  bundleInfo,
  onChoiceChange,
  isV2,
  crumbs,
}: ProductDetailsProps) => {
  const [accessoryPickerValues, setAccessoryPickerValues] = useState(null);
  const [quantity, setQuantity] = useState(1);

  const pdpItem = useSelector((state: any) => state.pdpItem);
  const site = useSelector((state: any) => state.site);
  const {
    details,
    autoApplyDiscount,
    variants,
    pageDesign: originalPageDesign,
    view,
    canMiyo,
    miyo,
    design,
    loading,
  } = pdpItem;

  const dispatch = useAppDispatch();

  const hasPageDesign = !!originalPageDesign;
  const isEnhancedLifestyleHeader = useVariation('Enhanced PDP - Lifestyle Header');
  const pageDesign = useMemo(() => {
    if (isEnhancedLifestyleHeader) {
      return {
        ...originalPageDesign,
        elements: originalPageDesign?.elements?.map((e) => {
          if (e.elementType === 'header') {
            return {
              ...e,
              position: 'after_item',
            };
          }

          return e;
        }),
      };
    }

    return originalPageDesign;
  }, [isEnhancedLifestyleHeader, originalPageDesign]);
  const { components = [], elements = [] } = pageDesign ?? {};

  const debug = useVariation('debug');

  const isMobView = useMediaQuery({ maxWidth: Breakpoints.MD });
  const isTabletView = useMediaQuery({ maxWidth: Breakpoints.LG });
  const isDesktopView = !isMobView && !isTabletView;

  const isBulkView = view === PDPViews.BULK;
  const isDesignView = canMiyo && view === PDPViews.DESIGN;
  const isDesignBundleView = !!bundleInfo?.bundle?.canMiyoMembers && !!bundleInfo?.customInfo?.isCustomizing;
  const isGalleryView = canMiyo && view === PDPViews.ASSET_PREVIEW;
  const isDefaultView = !isBulkView && !isDesignView && !isDesignBundleView && !isGalleryView;

  const shouldHideLifestyleImages = useVariation('enhanced-imagery');
  const isEnhancedPDPVariant = useVariation('Enhanced PDP');

  const hasFindYourFitCharts =
    !!v2Data.findYourFitCharts || elements.some((e) => e.elementType === 'findYourFit');

  // Combining some details and bundle data
  const stock = bundleInfo?.stockDetails?.stock ?? details?.stock;
  const processingDays = bundleInfo?.stockDetails?.processingDays ?? details?.processingDays ?? 0;
  const compositeSlides = bundleInfo?.compositeImageSlide ?? details?.compositeSlides;

  const images = useMemo(() => {
    if (!bundleInfo?.webImages || bundleInfo.compositeImageSlide.length > 0) {
      return {
        ...(details?.webImages || {}),
        ...(shouldHideLifestyleImages ? { lifestyle: [] } : {}),
      };
    }

    return {
      views: [bundleInfo.webImages.default].concat(details.webImages.views),
    };
  }, [bundleInfo?.webImages, bundleInfo?.compositeImageSlide, details?.webImages, shouldHideLifestyleImages]);
  const pricingItem = bundleInfo?.bundlePricing ?? details;
  const rate = pricingItem?.rate;
  const additionalSaleFormatted = details?.additionalSaleFormatted;
  const discountBanner = bundleInfo ? bundleInfo.bundle.discountBanner : details?.discountBanner;

  const bundlePromoBar = bundleInfo && (
    <ErrorBoundary
      inlineError
      message={
        <>
          Oops! Something went wrong while loading the bundle bar.
          <br />
          <br />
          We're working hard to fix it. Please try again shortly.
        </>
      }>
      <ProductBundleBar bundleInfo={bundleInfo} />
    </ErrorBoundary>
  );

  const discountPromoBar = discountBanner && (
    <ErrorBoundary
      inlineError
      message={
        <>
          Oops! Something went wrong while loading the discount bar.
          <br />
          <br />
          We're working hard to fix it. Please try again shortly.
        </>
      }>
      <div className='tw:mt-4'>
        <ProductDiscountBanner
          className='tw:rounded-[5px] tw:!text-left tw:lg:!px-9'
          discountBanner={discountBanner}
        />
      </div>
    </ErrorBoundary>
  );
  const productPromoBar = !discountPromoBar && autoApplyDiscount && (
    <ErrorBoundary
      inlineError
      message={
        <>
          Oops! Something went wrong while loading the promo bar.
          <br />
          <br />
          We're working hard to fix it. Please try again shortly.
        </>
      }>
      <ProductPromoBar autoApplyDiscount={autoApplyDiscount} />
    </ErrorBoundary>
  );

  const isLimitedEdition = details?.isLimitedEdition;
  const isNBCF = isLimitedEdition && details?.attributes?.colorId === 340;
  const limitedEditionPill = isNBCF && <NBCFLabel className='tw:absolute tw:right-9 tw:top-4' />;

  const miyoHeader = miyo?.partnerConfig?.partner?.logoUrl && (
    <div className='tw:mb-3'>
      <Link
        href={
          miyo.partnerConfig.partner.url ||
          jsRoutes.controllers.Customization.partner(miyo.partnerConfig.partner.slug).url
        }>
        <img
          src={miyo.partnerConfig.partner.logoUrl}
          className='tw:mx-auto tw:h-full tw:max-h-[84px] tw:md:max-h-[111px]'
        />
      </Link>
    </div>
  );

  const miyoOverride = details?.uom.id === 1;
  const isMiyoable = miyoOverride && canMiyo && !MIYO_DISABLED_COLORS.includes(variants?.activeColor?.id);

  const handleCustomizeClick = (e) => {
    e.preventDefault();

    if (typeof bundleInfo?.onCustomize === 'function') {
      bundleInfo.onCustomize(0);
    } else if (isMiyoable) {
      dispatch(setView(PDPViews.DESIGN));
    }
  };

  const shouldShowDimensions =
    !hasPageDesign && isV2 && (details?.dimensionSpecs?.sizes || details?.webImages?.dimensions);
  const renderedDescription = (
    <ErrorBoundary>
      <Description content={details?.description} />
    </ErrorBoundary>
  );

  const { hideSectionsNav, showDescription, showDimensions } = useMemo(() => {
    const pdpConfig = (elements || []).find((e) => e.elementType === 'pdpConfig');

    if (hasPageDesign) {
      return {
        hideSectionsNav: pdpConfig?.attributes?.hideSectionsNav,
        showDescription: pdpConfig?.attributes?.showDescription,
        showDimensions: pdpConfig?.attributes?.showDimensions,
      };
    }

    return {
      hideSectionsNav: false,
      showDescription: false,
      showDimensions: false,
    };
  }, [hasPageDesign, elements]);

  const renderedBundleChoices = Boolean(bundleInfo) && (
    <ErrorBoundary inlineError>
      <div className='tw:order-7'>
        {bundlePromoBar}
        <BundleChoices
          isInline={true}
          memberChoices={bundleInfo.bundle.memberChoices}
          selectedItems={bundleInfo.selectedItems}
          attributesByKey={bundleInfo.attributesByKey}
          itemsById={bundleInfo.itemsById}
          selectionTitle={bundleInfo.bundle.displayName}
          onChoiceChange={bundleInfo.onChoiceChange}
          onCustomize={bundleInfo.onCustomize}
          customInfo={bundleInfo?.customInfo}
        />
      </div>
    </ErrorBoundary>
  );

  return (
    <>
      {loading && <LoadingSpinner delay={SHOW_LOADER_DELAY} />}
      {isDefaultView && (
        <>
          {hasPageDesign && <Section position='before_header' {...{ components, elements, debug }} />}

          {!hideSectionsNav && <SectionsNav elements={elements} />}
          <Section position='after_header' type='page' {...{ components, elements, debug }} />

          {(details || bundleInfo) && (
            <ErrorBoundary silent>
              <PersistentAddToCart
                siteId={site.id}
                klaviyoConfigured={klaviyoConfigured}
                addOns={accessoryPickerValues?.selectedAddOns}
                isGenericProduct={details?.isGenericProduct}
                showPicker={variants?.showPicker}
                stock={stock}
                {...(bundleInfo
                  ? {
                      bundleInfo,
                      id: bundleInfo.bundle.id,
                      webImages: bundleInfo.bundle.webImages,
                      rate: bundleInfo.bundlePricing.rate,
                      displayName: bundleInfo.selectedItems[0].displayName,
                      typeName: bundleInfo?.bundle?.typeAttribute?.value,
                      stock: bundleInfo.stockDetails.stock,
                      isInlineBundle: true,
                    }
                  : {
                      id: details.id,
                      webImages: details.webImages,
                      rate: details.rate,
                      displayName: details.displayName,
                      stock,
                      typeName: details.attributes.itemType,
                    })}
              />
            </ErrorBoundary>
          )}

          <section
            className={cx('product-main tw:lg:!pt-6', {
              'tw:max-w-[1440px] tw:mx-auto': isV2,
            })}>
            <div
              className={cx({
                'container-fluid': isV2,
                container: !isV2,
              })}>
              <div className={cx('row', { 'px-lg-3': isV2 })}>
                {!isDesignView && (
                  <div className='col-12'>
                    <Breadcrumbs
                      crumbs={crumbs}
                      className='tw:hidden tw:capitalize tw:lg:!mb-5 tw:lg:flex tw:lg:!justify-start'
                    />
                  </div>
                )}

                <StickySection
                  className={cx('col-lg-7 tw:flex tw:justify-center', {
                    'col-xl-8 p-0': isV2,
                  })}>
                  <ErrorBoundary
                    message={
                      <>
                        Oops! Something went wrong while loading the images slider.
                        <br />
                        <br />
                        We're working hard to fix it. Please try again shortly.
                      </>
                    }>
                    {isTabletView && <div className='product-details'>{miyoHeader}</div>}

                    {details && (
                      <div className='tw:w-full tw:max-w-[100vw] tw:overflow-x-hidden tw:md:px-5'>
                        <div className='tw:mx-auto tw:mb-0 tw:max-w-md tw:lg:max-w-[1000px]'>
                          <ProductImageSlider
                            key={details.id}
                            images={images}
                            autoApplyDiscount={autoApplyDiscount}
                            productPromoBar={!!isMobView && productPromoBar}
                            stock={stock}
                            displayName={details.displayName}
                            prependSlides={v2Data.prependSlides}
                            discountLabel={additionalSaleFormatted}
                            compositeSlides={compositeSlides}
                            adaptiveHeight={!bundleInfo}
                            showLimitedEdition={!isDesktopView && isLimitedEdition}
                            isNBCF={isNBCF}
                            miyo={canMiyo && miyo}
                            isBundle={!!bundleInfo}
                          />
                        </div>
                      </div>
                    )}
                  </ErrorBoundary>
                </StickySection>
                <div className={cx('col-lg-5 product-details', { 'col-xl-4': isV2 })}>
                  <ProductInfoContainer>
                    <Section position='item_top' {...{ components, elements, debug }} />
                    {details && site && (
                      <script
                        type='application/ld+json'
                        dangerouslySetInnerHTML={{
                          __html: JSON.stringify({
                            '@context': 'https://schema.org/',
                            '@type': 'Product',
                            name: details.displayNameOnPage,
                            description: stripHTML(details.description),
                            image: details.webImages.default.active.url,
                            brand: {
                              '@type': 'Brand',
                              name: site.name,
                            },
                            aggregateRating: {
                              '@type': 'AggregateRating',
                              ratingValue: details.rating.rating,
                              reviewCount: details.rating.count,
                            },
                            offers: {
                              '@type': 'Offer',
                              availability: `https://schema.org/${stock === 'soldOut' ? 'OutOfStock' : stock === 'preOrder' ? 'PreOrder' : 'InStock'}`,
                              price: pricingItem.rate,
                              priceSpecification: {
                                '@type': 'UnitPriceSpecification',
                                price: pricingItem.rate,
                                priceCurrency: 'USD',
                                referencePrice: {
                                  '@type': 'UnitPriceSpecification',
                                  price: pricingItem.ourPrice ?? pricingItem.rate,
                                  priceCurrency: 'USD',
                                },
                              },
                              priceCurrency: 'USD',
                            },
                          }),
                        }}
                      />
                    )}

                    {isTabletView && (
                      <ErrorBoundary
                        message={
                          <>
                            Oops! Something went wrong while loading the mobile header.
                            <br />
                            <br />
                            We're working hard to fix it. Please try again shortly.
                          </>
                        }>
                        <MobileHeader
                          pricingItem={pricingItem}
                          addOns={accessoryPickerValues?.selectedAddOns}
                          includeAggregateRating={false}
                        />
                      </ErrorBoundary>
                    )}

                    {isDesktopView && limitedEditionPill}

                    {!isTabletView && (
                      <ErrorBoundary
                        inlineError
                        message={
                          <>
                            Oops! Something went wrong while loading the customization header.
                            <br />
                            <br />
                            We're working hard to fix it. Please try again shortly.
                          </>
                        }>
                        <div>{miyoHeader}</div>
                      </ErrorBoundary>
                    )}
                    <ProductHeader
                      tagline={bundleInfo?.bundle?.tagline ?? v2Data?.tagline}
                      includeAggregateRating={false}
                    />

                    {!isMobView && productPromoBar}

                    <FreeGiftCta />

                    {!hasPageDesign && (
                      <>
                        <DrinkwareSubtitle />
                      </>
                    )}

                    <Section position='item_content' {...{ components, elements, debug }} />

                    <ErrorBoundary
                      inlineError
                      message={
                        <>
                          Oops! Something went wrong while loading the color swatches.
                          <br />
                          <br />
                          We're working hard to fix it. Please try again shortly.
                        </>
                      }>
                      <>
                        {!!design || !!bundleInfo?.customInfo?.designs?.[0] ? (
                          <div className='tw:mb-6 tw:flex tw:flex-col tw:gap-3'>
                            <EditPersonalization onClick={handleCustomizeClick} />
                            {renderedBundleChoices}
                            <AccessoryUpsell className='tw:order-8' />
                          </div>
                        ) : (
                          <>
                            {discountPromoBar}

                            <div className='variants purchase-section tw:pb-5'>
                              <ProductSwatches
                                onCustomize={bundleInfo?.bundle?.isInline ? handleCustomizeClick : undefined}
                                showAccessoryPickers={!bundleInfo}
                                setAccessoryPickerValues={setAccessoryPickerValues}
                                dimensionsScroll={
                                  <>
                                    {(details?.webImages?.dimensions ||
                                      details?.dimensionSpecs?.sizes ||
                                      hasFindYourFitCharts) && (
                                      <div className='tw:relative tw:z-10 tw:-mb-10 tw:flex tw:justify-end'>
                                        <SectionScroll
                                          sectionId={
                                            hasFindYourFitCharts ? 'FindYourFit' : 'ProductComparison'
                                          }
                                          icon={<CgRuler size={24} />}
                                          text={hasFindYourFitCharts ? 'Find Your Fit' : 'Dimensions'}
                                        />
                                      </div>
                                    )}
                                  </>
                                }
                                addendum={renderedBundleChoices}
                              />
                            </div>
                          </>
                        )}
                      </>
                    </ErrorBoundary>

                    <ErrorBoundary inlineError>
                      <div
                        className='checkout'
                        itemProp='offers'
                        itemScope
                        itemType='http://schema.org/Offer'>
                        {details && site && variants && (
                          <React.Fragment>
                            <link itemProp='url' href={`${site.url}${details.url}`} />
                            <ProductPricing
                              siteId={site.id}
                              item={pricingItem}
                              addOns={accessoryPickerValues?.selectedAddOns}
                              {...{ design, miyo, bundleInfo, quantity, setQuantity, stock }}
                            />
                            {bundleInfo ? (
                              <AddToCart
                                id={bundleInfo.bundle.id}
                                webImages={bundleInfo.bundle.webImages}
                                rate={bundleInfo.bundlePricing.rate}
                                displayName={bundleInfo.bundle.displayName}
                                bundleItems={bundleInfo.selectedItems}
                                bundleCustomInfo={bundleInfo.customInfo}
                                stock={bundleInfo.stockDetails.stock}
                                siteId={site.id}
                                isInlineBundle
                                quantity={quantity}
                                setQuantity={setQuantity}
                              />
                            ) : (
                              <AddToCart
                                id={details.id}
                                webImages={details.webImages}
                                rate={details.rate}
                                displayName={details.displayName}
                                isGenericProduct={details.isGenericProduct}
                                showPicker={variants.showPicker}
                                stock={stock}
                                klaviyoConfigured={klaviyoConfigured}
                                addOns={accessoryPickerValues?.selectedAddOns}
                                relatedItems={details.relatedItems}
                                siteId={site.id}
                                quantity={quantity}
                                setQuantity={setQuantity}
                              />
                            )}
                            {isEnhancedPDPVariant && (
                              <ErrorBoundary silent>
                                <Affirm
                                  isGenericProduct={details.isGenericProduct}
                                  isOutOfStock={stock === 'soldOut'}
                                  isCustomShop={false}
                                  upcCode={details.upcCode}
                                  item={details}
                                  addOns={accessoryPickerValues?.selectedAddOns}
                                  className='tw:-mt-2'
                                />
                              </ErrorBoundary>
                            )}

                            <TieredDiscount className='tw:my-4' />

                            <Section position='item_cart' {...{ components, elements, debug }} />
                            <div className='tw:grid tw:grid-cols-2 tw:gap-4'>
                              <ProductAvailability
                                className='tw:w-full'
                                quantityPerOrderLimit={details.quantityPerOrderLimit}
                                stock={stock}
                                processingDays={processingDays}
                              />

                              <ProductShipping
                                className='tw:w-full'
                                freeEconomySubtotal={site.shipping.freeEconomySubtotal}
                                itemRate={rate}
                              />

                              {details?.warrantyPeriod && (
                                <ProductWarranty
                                  warrantyPeriod={details?.warrantyPeriod}
                                  className='tw:col-span-2'
                                />
                              )}
                            </div>
                          </React.Fragment>
                        )}
                      </div>
                    </ErrorBoundary>

                    {!hasPageDesign && !isV2 && details && site?.id !== 2 && (
                      <div className='description'>
                        <p className='mt-4' dangerouslySetInnerHTML={{ __html: details.description }}></p>
                      </div>
                    )}

                    {!hasPageDesign && !isV2 && (
                      <ErrorBoundary>
                        <div className='tw:mt-5 tw:border-t tw:border-gray-300 tw:pt-5'>
                          <SectionScroll
                            sectionId='TechAndFeatures'
                            icon={<BsGear size={18} className='tw:text-[#303030]' />}
                            text='Tech & Features'
                            textClassName='tw:font-bold tw:text-[#303030]'
                          />
                        </div>
                      </ErrorBoundary>
                    )}

                    <Section position='item_bottom' {...{ components, elements, debug }} />
                  </ProductInfoContainer>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
      {isBulkView && (
        <React.Suspense fallback={<LoadingSpinner />}>
          <BulkDesigner />
        </React.Suspense>
      )}
      {isDesignView && (
        <React.Suspense fallback={<LoadingSpinner />}>
          <CustomDesigner />
        </React.Suspense>
      )}
      {isDesignBundleView && (
        <React.Suspense fallback={<LoadingSpinner />}>
          <BundleDesigner {...{ bundleInfo, onChoiceChange }} />
        </React.Suspense>
      )}
      {isGalleryView && <PartnerDesignsView />}

      {isDefaultView && (
        <>
          {(isV2 || showDescription) && renderedDescription}
          {showDimensions && <Dimensions />}

          <Section position='after_item' key={details?.id} type='page' {...{ components, elements, debug }} />

          {shouldShowDimensions && (
            <div className='tw:py-5 tw:mx-auto tw:max-w-6xl tw:px-0'>
              {isV2 && (
                <h2 className='tw:font-title tw:text-2xl tw:font-bold tw:text-body tw:text-center tw:mb-0'>
                  Dimensions
                </h2>
              )}

              {!isV2 && renderedDescription}

              <Dimensions />
            </div>
          )}

          {!hasPageDesign && v2Data.findYourFitCharts && (
            <ErrorBoundary>
              <FindYourFit charts={v2Data.findYourFitCharts} note={v2Data?.findYourFitNote} />
            </ErrorBoundary>
          )}

          {!hasPageDesign && v2Data.featureCards && v2Data.featureCards?.items?.length > 0 && (
            <ErrorBoundary>
              <AnimatedSlider
                key={details?.id}
                id='Features'
                slides={v2Data.featureCards.items.map((card, idx) => ({
                  template: AnimatedSlideTemplate.IMAGE_CONTENT,
                  image: {
                    desktop: card.imageUrl,
                    mobile: card?.imageUrlMob || card.imageUrl,
                  },
                  video: card?.videoUrl,
                  copy: {
                    tagline: v2Data.featureCards.title,
                    title: card.title,
                    description: card.description,
                  },
                }))}
              />
            </ErrorBoundary>
          )}

          {details?.relatedItems?.length > 0 && (
            <ErrorBoundary>
              <ProductsSlider
                starsColor={site.id === 2 ? 'var(--brand-black)' : '#ffb829'}
                starsSize='16px'
                showAddToCart
                products={details.relatedItems}
                title='Recommended Products'
              />
            </ErrorBoundary>
          )}

          <Section position='before_reviews' type='page' {...{ components, elements, debug }} />

          <ErrorBoundary>
            <ProductReviews />
          </ErrorBoundary>

          <Section position='after_reviews' type='page' {...{ components, elements, debug }} />
        </>
      )}
    </>
  );
};

export default ProductDetails;
